/**
 * @fileoverview Página responsável por explorar e administrar arquivos compartilhados.
 */

import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Box,
} from "@material-ui/core";
import { Pencil, Trash2 as Trash, Plus, Search, File } from "lucide-react";
import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_TAG") {
        const fileListId = action.payload;

        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(4),
    },
    title: {
        fontSize: "1.75rem",
        fontWeight: 700,
        color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
    },
    searchField: {
        flex: 1,
        maxWidth: 400,
        marginRight: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px',
            background: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover': {
                background: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
                border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.25)' : '1px solid rgba(61, 92, 253, 0.15)',
            },
            '&.Mui-focused': {
                background: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
                border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.4)' : '1px solid rgba(61, 92, 253, 0.3)',
                boxShadow: '0 0 0 1px rgba(91, 124, 255, 0.15)',
            },
        },
    },
    addButton: {
        borderRadius: "8px",
        textTransform: "none",
        fontWeight: 500,
        padding: "10px 24px",
    },
    card: {
        background: theme.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.6) 0%, rgba(22, 22, 24, 0.6) 100%)'
            : '#FFFFFF',
        border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: "20px",
        boxShadow: theme.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
            transform: 'translateY(-4px)',
            boxShadow: theme.mode === 'dark'
                ? '0 12px 40px rgba(91, 124, 255, 0.25)'
                : '0 8px 30px rgba(61, 92, 253, 0.15)',
            border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(61, 92, 253, 0.2)',
        },
    },
    cardContent: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    fileIcon: {
        fontSize: "3rem",
        color: theme.mode === 'dark' ? '#5B7CFF' : '#3D5CFD',
    },
    fileName: {
        fontSize: "1.1rem",
        fontWeight: 600,
        color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
    },
    cardActions: {
        padding: theme.spacing(2),
        paddingTop: 0,
        justifyContent: "flex-end",
    },
}));

const FileLists = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    const socketManager = useContext(SocketContext);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        const socket = socketManager.getSocket(user.companyId);

        socket.on(`company-${user.companyId}-file`, (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_USER", payload: +data.fileId });
            }
        });

        return () => {
            socket.disconnect();
        };
    }, [socketManager, user]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <MainContainer className={classes.root}>
            <ConfirmationModal
                title={deletingFileList && `${i18n.t("files.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />

            <div className={classes.header}>
                <Typography className={classes.title}>
                    {i18n.t("files.title")} ({files.length})
                </Typography>
                <Box display="flex" alignItems="center">
                    <TextField
                        className={classes.searchField}
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Plus />}
                        onClick={handleOpenFileListModal}
                        className={classes.addButton}
                    >
                        {i18n.t("files.buttons.add")}
                    </Button>
                </Box>
            </div>

            <Grid container spacing={3}>
                {files.map((fileList) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={fileList.id}>
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <File className={classes.fileIcon} />
                                <Typography className={classes.fileName}>
                                    {fileList.name}
                                </Typography>
                            </CardContent>
                            <CardActions className={classes.cardActions}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditFileList(fileList)}
                                >
                                    <Pencil size={18} strokeWidth={1.5} color="#5490ff" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => {
                                        setConfirmModalOpen(true);
                                        setDeletingFileList(fileList);
                                    }}
                                >
                                    <Trash size={18} strokeWidth={1.5} color="#c72b40" />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </MainContainer>
    );
};

export default FileLists;
